
import {
  defineComponent,
  ref,
  useFetch,
  useRoute,
  useRouter,
  computed,
  useMeta,
  useContext,
} from '@nuxtjs/composition-api';
import { useSimilarProducts, useFetchProductShops } from '@/composables';
import { useFetchProduct } from '@webplatform/nuxt-cms-js-sdk';
import prepareImagesWithAlts from '~/helpers/prepareImagesWithAlts';

export default defineComponent({
  setup() {
    const {
      error,
      $api: { documentsService },
    } = useContext();
    const route = useRoute();
    const router = useRouter();
    const {
      loading,
      fetchProductShops,
      productShops: shops,
    } = useFetchProductShops();
    const slug = computed(() => route.value.params.slug);
    const productDocuments = ref([]);
    const productDrivers = ref(null);
    const modalDrivers = ref(null);
    const modalDocuments = ref(null);
    const modalShops = ref(null);
    const {
      activeSimilarProduct,
      currentVariation,
      variations,
      similarProducts,
      setVariation,
      setProduct,
    } = useSimilarProducts();
    const { product, fetchProduct } = useFetchProduct(slug.value);
    const doFetchProduct = async () => {
      try {
        await fetchProduct();
        setProduct(product.value);
      } catch (_) {
        error({ statusCode: 404 });
      }
    };
    const breadcrumbs = computed(() => {
      if (!product.value) return [];
      const items = [];
      const categories = [...product.value.category_branch].reverse();
      categories.forEach((item, i) => {
        items.push({
          title: item.name,
          link: `${i === 0 ? '/product-category' : ''}${
            items[i - 1] ? items[i - 1].link : ''
          }/${item.slug}`,
        });
      });
      items.push({
        title: product.value.name,
      });
      return items;
    });
    const doFetchProductDocuments = async () => {
      const { data } = await documentsService.getAllDocuments({
        product_sku: product.value?.sku,
      });
      Object.entries(data).forEach((entry) => {
        const [key, value] = entry;
        if (['Drivers'].includes(key)) {
          productDrivers.value = value;
          return true;
        }
        productDocuments.value.push(...value);
        return true;
      });
    };

    const renderImageSliderFlag = computed(
      () =>
        variations.value &&
        typeof currentVariation.value === 'number' &&
        variations.value[currentVariation.value]?.images,
    );

    const preparedProductImages = computed(() => {
      const name = product.value?.name;
      const productVariations = variations.value;

      return productVariations && name
        ? prepareImagesWithAlts(
            productVariations[currentVariation.value].images,
            name,
          )
        : [];
    });

    useFetch(async () => {
      await doFetchProduct();
      await doFetchProductDocuments();
      product.value?.id && (await fetchProductShops(product.value.id));
    });
    const handleOpenShopsModal = () => {
      if (shops.value.length) modalShops.value.open();
      else router.push('/where-to-buy');
    };
    const isProductShopsShowed = computed(() => shops.value?.length);
    useMeta(() => ({
      title: product.value?.seo.page_title,
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content: product.value?.seo.description,
        },
        {
          property: 'og:title',
          content: product.value?.seo.page_title,
        },
      ],
    }));
    const peculiarities = computed(
      () => product.value?.content?.find((c) => c.type === 'bullets')?.data,
    );
    const productCharacteristics = computed(
      () => product.value?.feature_groups,
    );

    const prepareBlockData = (data) => {
      if (data === undefined || data === null) return {};
      return Array.isArray(data) ? { items: data } : data;
    };
    return {
      activeSimilarProduct,
      similarProducts,
      product,
      peculiarities,
      modalDrivers,
      modalDocuments,
      productCharacteristics,
      productDocuments,
      productDrivers,
      currentVariation,
      variations,
      breadcrumbs,
      setVariation,
      modalShops,
      loading,
      shops,
      handleOpenShopsModal,
      isProductShopsShowed,
      preparedProductImages,
      renderImageSliderFlag,
      prepareBlockData,
    };
  },
  head: {},
});
